import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HandCoins, Student, Wallet, Receipt, Clock, UserMinus, WarningCircle } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#0F6CBD', '#107C41', '#D83B01', '#FFB900', '#D13438', '#5C2D91'];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/stats').then(r => { setStats(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-slate-500 text-sm" data-testid="dashboard-loading">Loading dashboard...</div>;
  if (!stats) return <div className="text-red-500 text-sm">Failed to load dashboard</div>;

  const kpis = [
    { label: 'Total Donations', value: `$${(stats.total_donations || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, icon: HandCoins, color: '#0F6CBD' },
    { label: 'Active Students', value: stats.active_students || 0, icon: Student, color: '#107C41' },
    { label: 'Central Fund', value: `$${(stats.central_fund_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, icon: Wallet, color: '#D83B01' },
    { label: 'Total Expenses', value: `$${(stats.total_expenses || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, icon: Receipt, color: '#5C2D91' },
    { label: 'Pending Requests', value: stats.pending_requests || 0, icon: Clock, color: '#FFB900' },
    { label: 'Dropouts', value: stats.total_dropouts || 0, icon: UserMinus, color: '#D13438' },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <Card key={i} className="bg-white border border-slate-200 rounded-sm shadow-none" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">{kpi.label}</span>
                  <Icon size={18} color={kpi.color} weight="duotone" />
                </div>
                <div className="text-xl font-semibold text-slate-900 currency">{kpi.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Monthly Donations Chart */}
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-slate-800 font-['Chivo']">Monthly Donations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[...(stats.monthly_donations || [])].reverse()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
                  <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, 'Amount']} />
                  <Bar dataKey="total" fill="#0F6CBD" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Allocation Breakdown */}
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-slate-800 font-['Chivo']">Allocation Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center">
              {stats.allocation_breakdown?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={stats.allocation_breakdown.map(a => ({...a, type: a.type === 'central_fund' ? 'Central Fund' : a.type === 'students_via_history' ? 'Students (Historical)' : 'Students (Direct)'}))} dataKey="total" nameKey="type" cx="50%" cy="50%" outerRadius={90} label={({ type, total }) => `${type}: $${total.toLocaleString()}`}>
                      {stats.allocation_breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-slate-400">No allocation data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Items + Dropout Funds */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Pending alerts */}
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-slate-800 font-['Chivo']">Action Items</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats.pending_expenses > 0 && (
              <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-sm text-sm">
                <WarningCircle size={16} className="text-amber-600" />
                <span className="text-amber-800">{stats.pending_expenses} pending expense(s) await approval</span>
              </div>
            )}
            {stats.pending_requests > 0 && (
              <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-sm text-sm">
                <Clock size={16} className="text-blue-600" />
                <span className="text-blue-800">{stats.pending_requests} pending funding request(s)</span>
              </div>
            )}
            {stats.pending_expenses === 0 && stats.pending_requests === 0 && (
              <p className="text-sm text-slate-400">No pending items</p>
            )}
          </CardContent>
        </Card>

        {/* Dropout Funds */}
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-slate-800 font-['Chivo']">Dropout Funds</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(stats.dropout_funds || []).map(fund => (
              <div key={fund.fund_id} className="flex items-center justify-between p-2 border border-slate-100 rounded-sm">
                <span className="text-sm text-slate-700 font-medium">{fund.name}</span>
                <span className="currency text-sm text-slate-900">${(fund.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-slate-800 font-['Chivo']">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {(stats.recent_transactions || []).map((tx, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-slate-50 pb-2">
                  <div>
                    <span className="text-slate-700">{tx.description?.substring(0, 40)}</span>
                    <span className="text-xs text-slate-400 block">{tx.created_at?.substring(0, 10)}</span>
                  </div>
                  <span className="currency text-sm font-medium text-slate-900">${(tx.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
              {(!stats.recent_transactions || stats.recent_transactions.length === 0) && (
                <p className="text-sm text-slate-400">No recent transactions</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
